import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Download, FileArchive, FileSpreadsheet, FileText, Loader2, RefreshCw } from "lucide-react";
import { API, authHdr, FileField } from "./AssociateFields";

const X = `${API}/associate-extras`;

const save = async (url, name) => {
  const r = await fetch(url, { headers: authHdr() });
  if (!r.ok) throw new Error("Download failed");
  const href = URL.createObjectURL(await r.blob());
  const a = document.createElement("a");
  a.href = href; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(href);
};

const Tile = ({ icon: Icon, title, sub, onDownload, onGenerate, busy, tid }) => (
  <div className="flex items-center gap-3 rounded-md border p-3" data-testid={tid}>
    <Icon className="h-8 w-8 shrink-0 text-teal-700" />
    <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-gray-800">{title}</div>
      <div className="truncate text-xs text-gray-500">{sub}</div></div>
    {onGenerate && <Button size="sm" variant="outline" disabled={!!busy} onClick={onGenerate} data-testid={`${tid}-generate`}>
      {busy === "gen" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}<span className="ml-1 hidden sm:inline">Create new</span></Button>}
    <Button size="sm" className="bg-[#1a7f6f] hover:bg-[#166a5c]" disabled={!onDownload || !!busy} onClick={onDownload} data-testid={`${tid}-download`}>
      {busy === "dl" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}</Button>
  </div>
);

export const DownloadsTab = ({ id, ro, ctx, code, status, onActivated }) => {
  const [dl, setDl] = useState(null);
  const [busy, setBusy] = useState({});
  const load = () => axios.get(`${X}/${id}/downloads`, { headers: authHdr() }).then((r) => setDl(r.data.downloads)).catch(() => setDl({}));
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  const run = async (key, kind, fn) => {
    setBusy((b) => ({ ...b, [key]: kind }));
    try { await fn(); } catch (e) { toast.error(e.response?.data?.detail || e.message || "Failed"); } finally { setBusy((b) => ({ ...b, [key]: null })); }
  };
  const file = (key, url, msg) => () => run(key, "dl", async () => {
    let meta = dl?.[key];
    if (!meta) {
      meta = (await axios.post(url, {}, { headers: authHdr(), timeout: 120000 })).data;
      setDl((d) => ({ ...d, [key]: meta }));
      toast.success(`${msg} created`);
    }
    await save(`${API}/associates/${id}/files/${meta.storage_path}`, meta.name);
  });
  const gen = (key, url, msg) => () => {
    if (dl?.[key] && !window.confirm(`Create a fresh ${msg}? The current one will be replaced.`)) return;
    run(key, "gen", async () => { const r = await axios.post(url, {}, { headers: authHdr(), timeout: 120000 }); setDl((d) => ({ ...d, [key]: r.data })); toast.success(`${msg} created`); });
  };
  const put = async (patch) => {
    await axios.put(`${X}/${id}/downloads`, patch, { headers: authHdr() });
    setDl((d) => ({ ...d, ...patch }));
  };
  if (!dl) return <div className="flex h-32 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  return (
    <div className="space-y-8" data-testid="associate-downloads-tab">
      <section className="space-y-3">
        <h3 className="text-xs font-bold uppercase tracking-wider text-teal-700">Download</h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Tile icon={FileSpreadsheet} title="Download BSE File" sub={dl.bse_file?.name || `${code || "associate"}.csv — created when you download`} tid="dl-bse-file" busy={busy.bse_file}
            onDownload={file("bse_file", `${X}/${id}/bse-file`, "BSE file")} onGenerate={!ro ? gen("bse_file", `${X}/${id}/bse-file`, "BSE file") : null} />
          <Tile icon={FileText} title="Download Empanelment Form" sub={dl.empanelment_form?.name || "PDF — created when you download"} tid="dl-empanelment" busy={busy.empanelment_form}
            onDownload={file("empanelment_form", `${X}/${id}/empanelment-form`, "Empanelment Form")} onGenerate={!ro ? gen("empanelment_form", `${X}/${id}/empanelment-form`, "Empanelment Form") : null} />
          <Tile icon={FileArchive} title="All documents (ZIP)" sub="Every uploaded document in one file" tid="dl-zip" busy={busy.zip}
            onDownload={() => run("zip", "dl", () => save(`${X}/${id}/zip`, `${code || "associate"}_documents.zip`))} />
          <Tile icon={FileSpreadsheet} title="Associate details (Excel)" sub="Associate Master format, one row" tid="dl-excel" busy={busy.excel}
            onDownload={() => run("excel", "dl", () => save(`${X}/${id}/excel`, `${code || "associate"}_details.xlsx`))} />
        </div>
      </section>
      <section className="space-y-3">
        <h3 className="text-xs font-bold uppercase tracking-wider text-teal-700">Upload</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <Checkbox checked={!!dl.bse_uploaded} disabled={ro} data-testid="dl-bse-uploaded"
              onCheckedChange={(c) => put({ bse_uploaded: !!c }).catch(() => toast.error("Save failed"))} />File uploaded to BSE
          </label>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Signed Associate Form</label>
            <FileField fl={{ key: "associate_form", label: "Associate Form" }} value={dl.associate_form} ro={ro} ctx={ctx}
              onChange={(m) => put({ associate_form: m }).then(() => toast.success("Associate Form saved")).catch(() => toast.error("Save failed"))} />
          </div>
        </div>
      </section>
      {status === "Pending for Empanelment Form" && !ro && <ActivateBar id={id} dl={dl} onActivated={onActivated} />}
    </div>
  );
};

// Final step: needs both files created, the BSE tick and the signed form; then creates the login, emails it and marks Active.
const ActivateBar = ({ id, dl, onActivated }) => {
  const [busy, setBusy] = useState(false);
  const todo = [["Download the BSE file", dl.bse_file], ["Download the Empanelment Form", dl.empanelment_form],
    ["Tick 'File uploaded to BSE'", dl.bse_uploaded], ["Upload the signed Associate Form", dl.associate_form?.storage_path]];
  const ready = todo.every(([, ok]) => ok);
  const go = async () => {
    if (!window.confirm("Activate this associate and email their login details?")) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/associates/${id}/activate`, {}, { headers: authHdr(), timeout: 60000 });
      toast.success(r.data.message, { duration: 12000 });
      if (onActivated) onActivated();
    } catch (e) { toast.error(e.response?.data?.detail || "Activation failed"); } finally { setBusy(false); }
  };
  return (
    <section className="rounded-xl border border-teal-200 bg-teal-50/50 p-4" data-testid="associate-activate-bar">
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-teal-700">Activate associate</h3>
      <ul className="mb-3 grid gap-1 text-sm sm:grid-cols-2">
        {todo.map(([label, ok]) => (
          <li key={label} className={ok ? "text-emerald-700" : "text-gray-500"}>{ok ? "✓" : "○"} {label}</li>))}
      </ul>
      <Button onClick={go} disabled={!ready || busy} className="bg-teal-700 text-white hover:bg-teal-800" data-testid="associate-activate-btn">
        {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Submit &amp; Activate
      </Button>
      {!ready && <span className="ml-3 text-xs text-gray-500">Finish the steps above to enable.</span>}
    </section>);
};
